import { Business, FormatColorReset, HotTub, KeyboardArrowLeft, PowerSettingsNew, Settings } from '@mui/icons-material';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Popover from '@mui/material/Popover';
import Tooltip from '@mui/material/Tooltip';
import useTheme from '@mui/material/styles/useTheme';
import { type ReactNode, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { type Location, useLocation, useNavigate } from 'react-router-dom';
import SwipeableViews from 'react-swipeable-views';
import { setUserSettings as setUserSettingsProps } from '../_flux/Actions';
import { type FluxStore } from '../_flux/Store';
import { getUsers, setUserSettings } from '../_tools/AxiosVars';
import { getFirmaAlias, getFirmaName, getIdFirma, getIdUser, getRoute, getSwitchUsers, getUserImage, getUserName, getVertretungSeit, isDebug, isProgrammierung, setConf } from '../_tools/Session';
import Toast from '../_tools/Toast';
import { betterUseNavigate, checkUndefinedOrNull, isLocalServer } from '../_tools/Tools';
import InputCheck from '../_tools/inputfields/Check';
import Iconify from '../components/iconify/component';
import EinstellungenDashboard from '../dashboard/settings/EinstellungenDashboard';
import { type UserSettings } from '../login/EnsureLoggedInContainer';
import { type CustomTheme } from '../provider/ThemeProvider';

const UserMenu = () => {
    const unchangedData = useSelector((state: FluxStore) => state.unchangedData);
    const userSettings = useSelector((state: FluxStore) => state.userSettings);
    const dispatch = useDispatch();

    const [showUserMenu, setShowUserMenu] = useState<boolean>(false);
    const [showUserSettings, setShowUserSettings] = useState<boolean>(false);
    const [showFirmaChange, setShowFirmaChange] = useState<boolean>(false);
    const [userStatus, setUserstatus] = useState<ReactNode>(undefined);
    const navigate = useNavigate();
    const loc: Location & { state: { [index: string]: string | number | boolean } } = useLocation();
    const theme: CustomTheme = useTheme();

    const getUserStatus = () => {
        if (!getIdFirma() || !getIdUser()) return;
        getUsers().then(({ data }) => {
            let mitarbeiter = undefined;
            for (let i = 0; i < data.length; i++) {
                if (data[i]?.name === getFirmaName()) {
                    for (let j = 0; j < data[i]?.user.length; j++) {
                        if (data[i]?.user[j].id === getIdUser()) mitarbeiter = data[i]?.user[j];
                    }
                }
            }
            setUserstatus(
                mitarbeiter ? (
                    mitarbeiter.isvertreten ? (
                        <Tooltip title="Vertretung aktiv">
                            <Iconify color="error.main" height="100%" icon="mdi:minus-circle" />
                        </Tooltip>
                    ) : (
                        <Tooltip title="Online">
                            <Iconify color="success.main" height="100%" icon="mdi:circle" />
                        </Tooltip>
                    )
                ) : undefined
            );
        });
    };

    const changeFirma = (userId) => {
        betterUseNavigate(navigate, loc?.state, { replace: false, pathname: '/loginr', state: { changeFirma: userId, redir: getRoute(loc.pathname) } }, unchangedData);
    };

    const changeDarkMode = () => {
        const mainSettings: UserSettings['main'] = {
            ...userSettings?.main,
            darkMode: !userSettings?.main?.darkMode,
        };
        const newUserSettings: UserSettings = {
            ...userSettings,
            main: mainSettings,
        };

        setUserSettings(newUserSettings)
            .then(() => {
                setConf(newUserSettings);
                dispatch(setUserSettingsProps(newUserSettings));
            })
            .catch(() => Toast.addError({ key: 'UserMenu_setUserSettings', primary: 'Fehler beim Ändern des Anzeigemodus', secondary: 'Bitte versuchen Sie es in ein paar Sekunden erneut' }));
    };

    const changeSounds = () => {
        const mainSettings: UserSettings['main'] = {
            ...userSettings?.main,
            sounds: !userSettings?.main?.sounds,
        };
        const newUserSettings: UserSettings = {
            ...userSettings,
            main: mainSettings,
        };

        setUserSettings(newUserSettings)
            .then(() => {
                setConf(newUserSettings);
                dispatch(setUserSettingsProps(newUserSettings));
            })
            .catch(() => Toast.addError({ key: 'UserMenu_setUserSettings', primary: 'Fehler beim Ändern der Soundeinstellung', secondary: 'Bitte versuchen Sie es in ein paar Sekunden erneut' }));
    };

    const resetUserSettings = () => {
        setUserSettings(undefined, true)
            .then(() => {
                setConf(undefined);
                dispatch(setUserSettingsProps({ ...userSettings, reset: true }));
            })
            .catch(() => Toast.addError({ key: 'UserMenu_resetUserSettings', primary: 'Zurücksetzen der Benutzereinstellungen', secondary: 'Bitte versuchen Sie es in ein paar Sekunden erneut' }));
    };

    useEffect(function () {
        getUserStatus();
    }, []);

    const settings = userSettings?.main;

    return (
        <div id="user" style={{ maxWidth: 350 }}>
            <ListItemButton color="primary" onClick={() => setShowUserMenu(true)}>
                <ListItemIcon>
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={userStatus}
                        sx={{ '& .MuiBadge-badge': { backgroundColor: '#fff', height: 16, minWidth: 0, padding: '1px', width: 16 } }}
                    >
                        <Avatar style={{ backgroundColor: '#fff', color: theme?.palette?.primary?.main }} src={getVertretungSeit() ? undefined : getUserImage()}>
                            {getVertretungSeit() ? (
                                <HotTub />
                            ) : getUserName() ? (
                                getUserName()
                                    .split(' ')
                                    .map((namepart) => namepart.trim().charAt(0))
                            ) : undefined}
                        </Avatar>
                    </Badge>
                </ListItemIcon>
                <ListItemText
                    primary={
                        <span style={{ whiteSpace: 'nowrap' }} data-testid="userName">
                            {getUserName() + (getVertretungSeit() ? ' | VERTRETUNG!' : '')}
                        </span>
                    }
                    secondary={
                        <Tooltip title={'Kundennummer: ' + getIdFirma()}>
                            <span style={{ whiteSpace: 'nowrap' }} data-testid="userFirma">
                                {getFirmaAlias()}
                            </span>
                        </Tooltip>
                    }
                    primaryTypographyProps={{ style: { color: '#fff', fontSize: '0.9em', whiteSpace: 'nowrap' } }}
                    secondaryTypographyProps={{ style: { color: '#fff', fontSize: '0.9em', whiteSpace: 'nowrap' } }}
                    sx={{ display: { xs: 'none', md: 'block' } }}
                />
            </ListItemButton>
            <Popover
                elevation={4}
                open={showUserMenu}
                onClose={() => {
                    setShowUserMenu(false);
                    setShowUserSettings(false);
                    setShowFirmaChange(false);
                }}
                anchorEl={document.getElementById('user')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ style: { width: 270 } }}
            >
                <SwipeableViews index={showFirmaChange || showUserSettings ? 1 : 0}>
                    <List>
                        <ListItemButton disabled sx={{ display: { xs: 'flex', lg: 'none' }, fontSize: '0.9em', justifyContent: 'center' }}>
                            {getUserName()} | Kdnr.: {getIdFirma()}
                        </ListItemButton>
                        {getVertretungSeit() ? (
                            <ListItemButton disabled sx={{ display: { xs: 'flex', lg: 'none' } }}>
                                VERTRETUNG AKTIV!
                            </ListItemButton>
                        ) : undefined}
                        {!checkUndefinedOrNull(getSwitchUsers()) ? (
                            <ListItemButton onClick={() => setShowFirmaChange(true)}>
                                <Business style={{ marginRight: 8 }} />
                                Firma wechseln
                            </ListItemButton>
                        ) : undefined}
                        <ListItemButton onClick={() => setShowUserSettings(true)}>
                            <Settings style={{ marginRight: 8 }} />
                            Einstellungen ändern
                        </ListItemButton>
                        <ListItemButton
                            onClick={() => {
                                betterUseNavigate(navigate, loc?.state, { replace: false, pathname: '/logout' }, unchangedData);
                                setShowUserMenu(false);
                            }}
                        >
                            <PowerSettingsNew style={{ marginRight: 8 }} />
                            Logout
                        </ListItemButton>
                    </List>
                    {showUserSettings ? (
                        <List>
                            <ListItemButton onClick={() => setShowUserSettings(false)}>
                                <KeyboardArrowLeft style={{ marginRight: 8 }} />
                                Zurück
                            </ListItemButton>
                            <ListItemButton disabled style={{ fontSize: '0.9em' }}>
                                Oberfläche
                            </ListItemButton>
                            <ListItemButton data-testid="darkmode" onClick={changeDarkMode}>
                                <InputCheck edit type="switch" icon="mdi:brightness-4" l="Dunkles Design" v={settings?.darkMode} />
                            </ListItemButton>
                            <ListItemButton data-testid="sounds" onClick={changeSounds}>
                                <InputCheck edit type="switch" icon="mdi:volume-high" l="Benachrichtigungston" v={settings?.sounds} />
                            </ListItemButton>
                            {location.href.includes('/dashboard') ? (
                                <>
                                    <ListItemButton key="dashboard" disabled style={{ fontSize: '0.9em' }}>
                                        Dashboard
                                    </ListItemButton>
                                    <EinstellungenDashboard key="settingsDashboard" />
                                </>
                            ) : undefined}
                            {isLocalServer() ? (
                                <ListItemButton disabled style={{ fontSize: '0.9em' }}>
                                    Benutzer
                                </ListItemButton>
                            ) : undefined}
                            {isLocalServer() || isDebug() || isProgrammierung() ? (
                                <ListItemButton data-testid="resetUserSettings" onClick={resetUserSettings}>
                                    <FormatColorReset style={{ marginRight: 8 }} />
                                    Benutzeranpassungen zurücksetzen
                                </ListItemButton>
                            ) : undefined}
                        </List>
                    ) : showFirmaChange ? (
                        <List>
                            <ListItemButton onClick={() => setShowFirmaChange(false)}>
                                <KeyboardArrowLeft style={{ marginRight: 8 }} />
                                Zurück
                            </ListItemButton>
                            {!checkUndefinedOrNull(getSwitchUsers()) ? (
                                getSwitchUsers().map((firma) => (
                                    <ListItemButton key={firma.id} onClick={() => changeFirma(firma.id)}>
                                        {firma.alias ? firma.alias : firma.firmaName}
                                    </ListItemButton>
                                ))
                            ) : (
                                <div />
                            )}
                        </List>
                    ) : (
                        <div />
                    )}
                </SwipeableViews>
            </Popover>
        </div>
    );
};

export default UserMenu;
